import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 8;
const POLL_INTERVAL = 2500;

export default function InspectionConfirmed() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading | success | failed
  const [details, setDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); return; }
    poll(0);
  }, [sessionId]);

  const poll = async (attempt) => {
    if (attempt >= MAX_POLLS) { setStatus('failed'); return; }
    try {
      const res = await fetch(`${API}/api/payments/stripe/status/${sessionId}`);
      const data = await res.json();
      setDetails(data);
      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (data.status === 'expired') {
        setStatus('failed');
      } else {
        setAttempts(attempt + 1);
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      }
    } catch {
      setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
    }
  };

  const meta = details?.metadata || {};

  if (status === 'loading') return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader className="w-12 h-12 text-[#094f6f] animate-spin mx-auto mb-4" />
        <p className="text-gray-600 font-medium">Confirming your payment…</p>
        <p className="text-sm text-gray-400 mt-1">Please wait, do not close this page</p>
      </div>
    </div>
  );

  if (status === 'failed') return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-xl p-10 border border-red-100">
        <XCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Payment Not Confirmed</h2>
        <p className="text-gray-500 mb-6 text-sm">The payment may have been cancelled or expired. No charge was made.</p>
        <Link to="/" className="inline-block text-white px-6 py-3 rounded-xl font-semibold text-sm"
          style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
          Back to Homepage
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-10 border border-green-100">
        {/* Logo */}
        <div className="flex items-center justify-center gap-0 mb-6">
          <span className="text-2xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
          <img src="/rentalx-x-new.svg" alt="X"
            style={{ height: '1.7rem', width: 'auto', marginLeft: '0.05em', marginBottom: '0.02em', verticalAlign: 'middle' }} />
        </div>

        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">Virtual Inspection Confirmed!</h2>
        <p className="text-gray-500 text-sm text-center mb-6">
          Payment of <strong>A$15</strong> received. A confirmation has been sent to your email.
        </p>

        {/* Booking summary */}
        <div className="bg-sky-50 rounded-xl p-5 space-y-2.5 mb-6 border border-sky-100">
          {[
            ['🏠 Property',  meta.property_name],
            ['📍 Location',  meta.property_city],
            ['🛏 Room Type', meta.room_type],
            ['📅 Date',      meta.inspection_date],
            ['🕐 Time',      meta.inspection_slot ? `${meta.inspection_slot} AEST` : null],
            ['📹 Platform',  meta.platform],
          ].filter(([,v]) => v).map(([label, val]) => (
            <div key={label} className="flex justify-between text-sm">
              <span className="text-gray-500">{label}</span>
              <span className="font-medium text-gray-900">{val}</span>
            </div>
          ))}
          <div className="pt-2 border-t border-sky-200 flex justify-between text-sm font-bold">
            <span className="text-gray-700">Inspection Fee Paid</span>
            <span className="text-green-600">A$15.00 ✓</span>
          </div>
        </div>

        <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-700 mb-6 border border-amber-100 text-center">
          💰 <strong>Refund reminder:</strong> This A$15 fee is fully refunded if you proceed to book the property.
        </div>

        <div className="space-y-2">
          <Link to="/student-dashboard"
            className="block w-full text-center text-white py-3 rounded-xl font-semibold text-sm"
            style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
            Go to My Dashboard →
          </Link>
          <Link to="/" className="block w-full text-center text-gray-400 text-sm py-2 hover:text-gray-600">
            Back to Homepage
          </Link>
        </div>
      </div>
    </div>
  );
}
